<script setup lang="ts">
  import type { ButtonType } from '@uikit/ui-kit/types/button';
  import { computed, watchEffect } from 'vue';
  import { useRoute } from 'vue-router/composables';
  import { isString } from 'lodash-es';
  import useIdpLogin from '@/common/composables/useIdpLogin';
  import useAuthStore from '@/common/store/pinia/auth';
  import { useIdpAuthStore } from '@/common/store/pinia/idpAuth';
  import { storeToRefs } from 'pinia';

  const props = withDefaults(defineProps<{
    domainName?: string,
    sessionId: '0' | '1',
    buttonType?: ButtonType[keyof ButtonType],
    idp?: string,
    autoLogin?: boolean
  }>(), {
    autoLogin: true,
    buttonType: 'ghost',
    idp: ''
  });

  const route = useRoute();
  const { save: saveIdpData } = useIdpLogin();
  const { storageAccessGranted, currentUser } = storeToRefs(useAuthStore());
  const idpAuthStore = useIdpAuthStore();
  const { loginUrls } = storeToRefs(idpAuthStore);

  const urls = computed(() => props.idp
    ? loginUrls.value.filter(url => url.id === props.idp)
    : loginUrls.value);
  const currentUrl = computed(() => urls.value.find(url => url.id === props.idp));

  const onStorageAccess = () => new Promise((resolve) => {
    if (storageAccessGranted.value) {
      resolve(true);
      return;
    }

    watchEffect(() => {
      if (storageAccessGranted.value) {
        resolve(true);
      }
    });
  });

  async function openLogin(url: string) {
    // If window opened in iframe, we need storage access first
    // at this point request is requested but waited for user interaction
    await onStorageAccess();

    saveIdpData({
      domain: props.domainName,
      projectId: isString(route.query.project_id) ? route.query.project_id : '',
      successRedirect: isString(route.query.redirectUrl) ? route.query.redirectUrl : '/',
      failureRedirect: route.path
    });

    const params = new URLSearchParams();
    params.set('x-session-id', props.sessionId);
    window.location.replace(`${url}?${params}`);
  }

  idpAuthStore.resetState();
  if (props.domainName) {
    idpAuthStore.fetchLoginUrls(props.domainName)
      .then(async () => {
        if (props.idp
          && !currentUser.value
          && currentUrl.value
          && props.autoLogin
        ) {
          await openLogin(currentUrl.value.url);
        }
      });
  }

  defineExpose({
    onStorageAccess,
    openLogin,
    urls
  });
</script>

<template>
  <div class="v-id-p-login">
    <el-button
      v-for="idpLogin in urls"
      :key="idpLogin.name"
      class="mt-16"
      :data-testid="idpLogin.name"
      fluid
      :type="buttonType"
      @click="openLogin(idpLogin.url)"
    >
      {{ $t('Sign in with {name}', idpLogin) }}
    </el-button>
  </div>
</template>
